import { useEffect, useMemo, useRef, useState } from "react";
import { getLocale, history } from "umi";
import { useInfiniteScroll } from "ahooks";
import moment from "moment";
import { PageLogMes } from '@home/utils/buriedPointCfg';
import { buriedPointSearch, pointInter, typeIdObj } from "@/pages/home/utils/buriedPoint";
import { arrayRemoveElement } from "@intelligence/utils/common";
import { buildReportAuthors } from '@intelligence/components/ReportCard/authors';
import { highlightText } from '@/pages/dashboard/components/FilterSelect/utils';

interface Result {
  list: [];
  nextId: number | undefined;
}

interface IProps {
  systemId: string;
  studioId: string;
  gameId: string;
  searchType: string;
  pageLogMes?: any;
  resData: any;
  reportLogFn: (paramsObj: pointInter) => void;
}

export default (props: IProps, tabList: any[]) => {
  const { systemId, studioId, gameId, searchType, pageLogMes, resData = [], reportLogFn } = props;
  const locale = getLocale();
  const lazyScrollRef = useRef<HTMLDivElement>(null);
  const all = ["全部", "All"];
  const getDefaultActiveId = () => {
    const tab = history?.location?.query?.tab || '';
    if (tab) {
      const targetTab = tabList?.find(item => item.name === tab);
      return targetTab?.id || 'DataBrain';
    }
    return 'DataBrain';
  };

  const cacheSegmentValue = sessionStorage.getItem('__dbh_studio_reports_data_brain_tab__');

  const [dataFull, setDataFull] = useState<any>({});
  const [activeId, setActiveId] = useState(getDefaultActiveId());
  const [dataPartial, setDataPartial] = useState([]);
  const [dataShow, setDataShow] = useState([]);
  const [dataLoading, setLoading] = useState(true);
  const [segmentValue, setSegmentValue] = useState('All');
  const [languageOption, setLanguageOption] = useState({});
  const [option, setOption] = useState([]);
  /** 搜索关键词- undefined为默认值，不搜索，搜索后清空时值为空字符; 修复回退清空搜索时没有还原列表的bug */
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [cacheRecentSearch, setCacheRecentSearch] = useState([]);

  // 模拟接口请求获取数据，解决useInfiniteScroll渲染数据问题
  const getData = () => resData;
  /**
   * @description: 获得全部的Report列表
   * @return {*}
   */
  const getReportList = async () => {
    setLoading(true);
    try {
      const res = await getData();
      const tmp: any = {
        "DataBrain": [],
        "Archived Market Report": [],
        "Sensor Tower": [],
        "data.ai": [],
        "Stream Hatchet": [],
        "NPD": [],
        "Statista": [],
        "Famitsu": [],
        "Twitter": [],
        "Fandom": [],
        'Others': [],
      };
      const languageOption: any = {
        "DataBrain": {
          All: all,
          "Marketing": ["市场分析", "Marketing"],
          "IN-Game Analytic": ["游戏分析", "In-Game Analytics"],
        },
        "Archived Market Report": { All: all },
        "Sensor Tower": { All: all },
        "data.ai": { All: all },
        "Stream Hatchet": { All: all },
        "NPD": { All: all },
        "Statista": { All: all },
        "Famitsu": { All: all },
        "Twitter": { All: all },
        "Fandom": { All: all },
        'Others': { All: all },
      };
      const itmKeyList = Object.keys(tmp)?.map(item => item?.toLowerCase()) || [];
      if (Array.isArray(res?.resource_list) && res?.resource_list.length > 0) {
        res?.resource_list?.forEach((item: any, index: any) => {
          if (tmp?.[item?.source] || itmKeyList.includes(item?.source?.toLowerCase())) {
            const targetKey = Object.keys(tmp)?.find(config => config?.toLowerCase() === item?.source?.toLowerCase()) || '';
            const time = moment(item?.update_time).format('YYYY-MM-DD');
            const obj = {
              ...item,
              time,
              key: index,
              resource_id: item?.resource_id,
              coverImg: item?.cover,
              title: [item?.title, item?.title_en],
              originTitle: [item.title, item.title_en],
              report_type: [item?.report_type, item?.report_type_en],
              report_lang: [item?.report_language, item?.report_language_en],
              summary: [item?.introduction, item?.introduction_en],
              tag: [item?.tag, item?.tag_en],
              source: item?.source
            };
            if (item?.source?.toLowerCase() === 'databrain') {
              obj.creator_name = [item?.creator_name, item?.creator_name_en];
              obj.creator_head_img = item?.creator_head_picture;
              obj.creator_department = [item?.creator_department, item?.creator_department_en];
              obj.authors = buildReportAuthors(item);
              obj.report_like = item?.report_like;
              obj.update_time = item?.update_time;
            };
            tmp[targetKey]?.push(obj);
            if (!(item?.report_language_en in languageOption?.[targetKey]) && item?.source?.toLowerCase() !== 'databrain') {
              languageOption[targetKey][item.report_language_en] = [item?.report_language, item?.report_language_en];
            }
          }
        });
      }
      // 全量tab切换配置
      setLanguageOption(languageOption);
      // 获取tab数据（除databrain外都隐藏，databrain根据category字段归类）
      getOptionNew(languageOption, activeId);
      // 全量卡片数据
      setDataFull(tmp);
      // 根据默认的首选设置当前可搜索的Report列表
      setDataPartial(tmp[activeId]);
      // 展示的卡片数据
      setDataShow(tmp[activeId]);
      setLoading(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  // 实际展示的大类切换，无数据的不展示
  const showTabList = useMemo(() => {
    // 删除对象中空数组的数据
    const deleteArr: any = [];
    Object.keys(dataFull)?.forEach(key => {
      if (!dataFull?.[key] || dataFull?.[key]?.length === 0) {
        deleteArr?.push(key?.toLowerCase());
      }
    });
    const showTabList = tabList?.filter(v => !deleteArr?.includes(v?.id?.toLowerCase()));
    // 如果默认大类没数据，重置为有数据的第一个大类
    if (deleteArr?.includes(activeId?.toLowerCase())) {
      setActiveId(showTabList?.[0]?.id || "DataBrain");
    }
    return showTabList;
  }, [activeId, dataFull, tabList]);

  // 设置tab切换函数
  const getOptionNew = (languageOption: any, value: string, newLang?: number) => {
    if (!languageOption[value]) return;
    const finalOption: any = [];
    const langTmp = lang ?? newLang;
    Object.entries(languageOption[value])?.forEach(([k, v]) => {
      finalOption?.push({ value: k, text: v?.[langTmp] || k });
    });
    finalOption?.forEach((item: any, index: number) => {
      if (item?.value === 'All') {
        finalOption?.unshift(finalOption?.splice(index, 1)?.[0]);
      }
    });
    setOption(finalOption);
  };

  const debounce = useRef({ interval: 0, delay: 500 }).current;
  const onSearch = (word: string) => {
    clearTimeout(debounce.interval);
    // @ts-ignore
    debounce.interval = setTimeout(() => {
      searchFilterData(word);
    }, debounce.delay);
  };

  const searchFilterData = (word: string) => {
    if (cacheRecentSearch.indexOf(word) === -1 && word !== '') {
      const c = [...cacheRecentSearch];
      c.push(word);
      setCacheRecentSearch(c);
      localStorage.setItem(searchType, JSON.stringify(c));
    }
    onSearchRes(word);
    // 搜索打点
    if (pageLogMes?.pageId) {
      const extInfo = { pageId: pageLogMes?.pageId, activeId, segmentValue };
      buriedPointSearch(
        { extInfo: JSON.stringify(extInfo), typeId: pageLogMes?.typeId || typeIdObj.studio },
        word,
        gameId,
        "",
        'unified_id',
      );
    }
  };

  const getRecentSearchList = () => {
    try {
      const searchRecent = localStorage.getItem(searchType) || '[]';
      const searchRecentList = JSON.parse(searchRecent);
      const list = Array.isArray(searchRecentList) ? searchRecentList : [];
      setCacheRecentSearch(list);
    } catch (error) {
      console.log(error);
    }
  };

  const onClear = () => {
    onSearchRes("");
  };

  const onClickSearchRecent = (item) => {
    setSearch(item);
    onSearch(item);
  };

  const deleteSearchRecent = (e, index, item) => {
    const c = arrayRemoveElement(cacheRecentSearch, item);
    setCacheRecentSearch(c);
    localStorage.setItem(searchType, JSON.stringify(c));
  };

  // 处理segment下搜索的数据集合
  const segmentdataPartial = useMemo(() => {
    let segmentdataPartial: any = [];
    if (segmentValue === 'All') {
      segmentdataPartial = dataPartial?.slice(0) || [];
    } else {
      dataPartial?.forEach((item: any) => {
        // 旧studio专区report的tab归类字段category
        if (item?.report_lang?.[1] === segmentValue || item?.category === segmentValue) {
          segmentdataPartial?.push(item);
        }
      });
    }
    return segmentdataPartial;
  }, [dataPartial, segmentValue]);

  const onSearchRes = (word: string) => {
    let newData: any = [];
    // 搜索匹配(segment下搜索)
    if (typeof word === 'string' && word === '') {
      newData = segmentdataPartial;
    } else {
      segmentdataPartial?.forEach((item: { title: string[]; tag: string[]; }) => {
        let title = '';
        // 根据locale设置搜索的标题
        title = item.title[lang];
        const regex = new RegExp(`(${word.replace(/ /g, '\\s*')})`, 'gi');
        
        // 检查标题是否匹配
        const titleMatch = regex.test(title);
        
        // 检查标签是否匹配
        const tags = item.tag?.[lang] || [];
        const tagMatch = Array.isArray(tags) && tags.some((tag: string) => 
          new RegExp(`(${word.replace(/ /g, '\\s*')})`, 'gi').test(tag)
        );
        
        // 标题或标签匹配则加入结果
        if (titleMatch || tagMatch) {
          // 使用 highlightText 处理高亮（参考 IntelligenceReports）
          const highlightedTitle0 = titleMatch && lang === 0 ? highlightText(item.title[0], word) : item.title[0];
          const highlightedTitle1 = titleMatch && lang === 1 ? highlightText(item.title[1], word) : item.title[1];
          
          // 处理标签高亮
          const highlightedTag0 = tagMatch && lang === 0 && Array.isArray(item.tag[0])
            ? item.tag[0].map((tag: string) => highlightText(tag, word))
            : item.tag[0];
          const highlightedTag1 = tagMatch && lang === 1 && Array.isArray(item.tag[1])
            ? item.tag[1].map((tag: string) => highlightText(tag, word))
            : item.tag[1];
          
          const obj = { 
            ...item, 
            title: [highlightedTitle0, highlightedTitle1],
            tag: [highlightedTag0, highlightedTag1]
          };
          newData.push(obj);
        }
      });
    }
    setDataShow(newData);
  };

  function getLoadMoreList(nextId: number | undefined, limit: number): Promise<Result> {
    let start = 0;
    if (nextId) {
      start = dataShow.findIndex((i: any) => i?.key === nextId);
    }
    const end = start + limit;
    const list = dataShow?.slice(start, end);
    const nId = dataShow?.[end] ? dataShow?.[end]?.key : undefined;

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          list,
          nextId: nId,
        });
      }, 500);
    });
  }

  const { data, loading, loadingMore, noMore } = useInfiniteScroll(
    (d) => getLoadMoreList(d?.nextId, 6),
    {
      target: lazyScrollRef,
      isNoMore: (d) => d?.nextId === undefined,
      reloadDeps: [dataShow]
    },
  );

  /**
   * @description: 初始化一次性获得所有的Report列表
   * @return {*}
   */
  useEffect(() => {
    getReportList();
    getRecentSearchList();
    const targetTab = tabList?.find(item => item.id === activeId);
    if (targetTab) {
      if (PageLogMes?.[targetTab?.pagePoint]) {
        reportLogFn?.(PageLogMes?.[targetTab?.pagePoint]);
      }
    }
  }, []);

  /**
   * @description: 获得当前的语言
   * @return {*}
   */
  const lang = useMemo(() => {
    let lang = 0;
    if (locale === 'zh-CN') {
      lang = 0;
    } else {
      lang = 1;
    }
    getOptionNew(languageOption, activeId, lang);
    return lang;
  }, [locale]);

  /**
   * @description: 切换source来源tab时抹除搜索框和设置Report列表
   * @return {*}
   */
  useEffect(() => {
    setSearch('');
    setDataShow(dataFull[activeId]);
    setDataPartial(dataFull[activeId]);
    getOptionNew(languageOption, activeId);
    // 缓存当前tab选项选项
    let value = all[1];
    if (activeId !== 'DataBrain') {
      sessionStorage.removeItem('__dbh_studio_reports_data_brain_tab__');
    }else{
      value = cacheSegmentValue || value;
    }
    setSegmentValue(value);
  }, [activeId]);

  // tab切换设置展示的卡片数据
  useEffect(() => {
    setSearch('');
    let newData: any = [];
    if (segmentValue === 'All') {
      newData = dataPartial?.slice(0) || [];
    } else {
      dataPartial?.forEach((item: any) => {
        // 旧studio专区report的tab归类字段category
        if (item.report_lang?.[1] === segmentValue || item?.category === segmentValue) {
          newData.push(item);
        }
      });
    }
    setDataShow(newData);
  }, [dataPartial, segmentValue]);

  return {
    dataShow,
    showTabList,
    setActiveId,
    activeId,
    search,
    cacheRecentSearch,
    onSearch,
    onClear,
    onClickSearchRecent,
    deleteSearchRecent,
    lang,
    dataLoading,
    segmentValue,
    setSegmentValue,
    option,
    systemId,
    lazyScrollRef,
    data,
    loading,
    loadingMore,
    noMore
  };
};
